/**
 * Agent Workbench — attach ``code-connector`` by picking a verified row from
 * ``GET /tools/secrets-registry?toolName=code-connector`` (same contract as
 * {@link ./ToolGenericRestConfigWizard.jsx} attach mode for generic-rest-api).
 *
 * Inline git credential entry belongs under **Connections** (registry wizard);
 * this dialog only *mounts* an existing TESTED_OK secret onto the agent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { listSecretsRegistry, newToolInstanceId, secretsRegistryV1Enabled } from '../../services/toolsApi';

function registryRowAttachable(row, networkIdRaw) {
  const nid = String(networkIdRaw || '').trim();
  if (!nid || nid === 'new') return true;
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT') return true;
  if (k === 'NETWORK') {
    const rowNid = String(s.networkId ?? s.network_id ?? '').trim();
    return rowNid === nid;
  }
  return false;
}

function registrySecretTestPassed(row) {
  return String(row?.testStatus || '').trim().toUpperCase() === 'TESTED_OK';
}

/**
 * @param {{
 *   tool: { name: string, description?: string },
 *   open: boolean,
 *   isNewEntry: boolean,
 *   initialEntry?: { instanceId?: string, instanceLabel?: string, secretId?: string, secretName?: string },
 *   networkId: string,
 *   agentId: string,
 *   onSave: (entry: { name: string, instanceId: string, instanceLabel: string, secretId: string, secretName?: string }) => void,
 *   onCancel: () => void,
 * }} props
 */
export default function ToolCodeConnectorAgentConfigWizard({
  tool,
  open,
  isNewEntry,
  initialEntry,
  networkId,
  agentId,
  onSave,
  onCancel,
}) {
  const [instanceId] = useState(() => initialEntry?.instanceId || newToolInstanceId());

  const [registryRows, setRegistryRows] = useState([]);
  const [registryAttachLoading, setRegistryAttachLoading] = useState(false);
  const [registryAttachError, setRegistryAttachError] = useState('');
  const [selectedRegistryId, setSelectedRegistryId] = useState('');

  const registryRowsTestOk = useMemo(
    () => (Array.isArray(registryRows) ? registryRows.filter(registrySecretTestPassed) : []),
    [registryRows],
  );

  const unverifiedMountedRow = useMemo(() => {
    if (isNewEntry || !initialEntry?.secretId || !registryRows.length) return null;
    const sid = String(initialEntry.secretId || '').trim();
    const row = registryRows.find((r) => String(r.secretId || '').trim() === sid);
    if (!row || registrySecretTestPassed(row)) return null;
    return row;
  }, [isNewEntry, initialEntry?.secretId, registryRows]);

  useEffect(() => {
    if (!open || !tool?.name) return undefined;
    if (!secretsRegistryV1Enabled()) {
      setRegistryRows([]);
      setRegistryAttachError('');
      setRegistryAttachLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRegistryAttachLoading(true);
    setRegistryAttachError('');
    (async () => {
      try {
        // Pass networkId so the backend returns this network's inheritance chain
        // (TENANT + matching NETWORK). The unfiltered tenant surface now excludes
        // NETWORK rows (down-only), so omitting networkId here would hide a
        // network-scoped connection from its own attach picker.
        const _nid = String(networkId || '').trim();
        const listResult = await listSecretsRegistry({
          toolName: tool.name,
          networkId: _nid && _nid !== 'new' ? _nid : undefined,
        });
        const secrets = Array.isArray(listResult?.secrets) ? listResult.secrets : [];
        if (cancelled) return;
        const vis = secrets.filter((r) => registryRowAttachable(r, networkId));
        setRegistryRows(vis);

        const testOkRows = vis.filter(registrySecretTestPassed);
        const editingSid =
          !isNewEntry && initialEntry?.secretId ? String(initialEntry.secretId).trim() : '';

        let initialPick = '';
        if (editingSid) {
          const curRow = vis.find((r) => String(r.secretId || '').trim() === editingSid);
          if (curRow && registrySecretTestPassed(curRow)) {
            initialPick = editingSid;
          }
        }
        if (!initialPick && testOkRows.length >= 1) {
          initialPick = String(testOkRows[0].secretId || '');
        }
        setSelectedRegistryId(initialPick);
      } catch (e) {
        if (!cancelled) {
          setRegistryAttachError(e?.message || 'Could not load saved connections');
          setRegistryRows([]);
        }
      } finally {
        if (!cancelled) setRegistryAttachLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isNewEntry, tool?.name, networkId, initialEntry?.secretId]);

  const confirmRegistryAttach = () => {
    const row = registryRowsTestOk.find((r) => String(r.secretId) === String(selectedRegistryId));
    if (!row) {
      setRegistryAttachError('Pick a connection that passed Test in Connections.');
      return;
    }
    if (!registrySecretTestPassed(row)) {
      setRegistryAttachError('Only connections with Test OK can be mounted on an agent.');
      return;
    }
    setRegistryAttachError('');
    const label = String(row.secretName || '').trim() || String(row.secretId || '').slice(0, 8);
    onSave({
      name: tool.name,
      instanceId: isNewEntry ? instanceId : initialEntry?.instanceId || instanceId,
      instanceLabel: label,
      secretId: row.secretId,
      secretName: row.secretName,
    });
  };

  if (!open || !tool) return null;

  if (!secretsRegistryV1Enabled()) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tool-code-connector-title"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onCancel();
        }}
      >
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <h2 id="tool-code-connector-title" className="text-base font-semibold text-gray-900">
            Git connections unavailable
          </h2>
          <p className="mt-2 text-xs text-gray-600">
            The secrets registry is disabled in this environment. Enable{' '}
            <code className="rounded bg-gray-100 px-1">VITE_STEPFLOW_SECRETS_REGISTRY_V1</code> or use a deployment where Connections are supported.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tool-code-connector-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="flex w-full max-w-xl flex-col rounded-lg bg-white shadow-xl max-h-[90vh]">
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id="tool-code-connector-title" className="text-base font-semibold text-gray-900">
              Attach git connection
            </h2>
            <p className="mt-1 text-xs text-gray-600">
              Pick a saved <strong className="font-medium text-gray-800">code-connector</strong> from Connections. This is not the same as a parameterless MCP tool — the agent needs a verified repo credential. Create or edit connections under{' '}
              <strong className="font-medium text-gray-800">Connections</strong> first, run <strong className="font-medium text-gray-800">Test</strong> until it shows OK, then mount it here.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 space-y-3">
          <p className="text-xs text-gray-600">
            Reuse credentials stored for this tenant. Only connections that show <strong className="font-medium text-gray-800">TESTED_OK</strong> in Connections can be mounted.
          </p>
          {registryAttachLoading ? <p className="text-xs text-gray-500">Loading connections…</p> : null}
          {registryAttachError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {registryAttachError}
            </div>
          ) : null}
          {!registryAttachLoading && unverifiedMountedRow ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950">
              This agent&apos;s mount &quot;{String(unverifiedMountedRow.secretName || unverifiedMountedRow.secretId).slice(0, 80)}
              &quot; has not passed Connection Test — open Connections, run <strong className="font-medium">Test</strong>, then pick a verified connection below.
            </div>
          ) : null}
          {registryRows.length === 0 && !registryAttachLoading ? (
            <p className="text-xs text-gray-500">
              No git connections yet. Open <strong className="font-medium text-gray-700">Connections</strong>, add a <strong className="font-medium text-gray-700">code-connector</strong>, run Test, then return here.
            </p>
          ) : null}
          {registryRows.length > 0 && registryRowsTestOk.length === 0 && !registryAttachLoading ? (
            <p className="text-xs text-gray-500">
              Connections exist but none have <strong className="font-medium text-gray-700">Test OK</strong> yet. Open Connections and run Test until the row passes, then return.
            </p>
          ) : null}
          {registryRowsTestOk.length > 0 ? (
            <label className="block space-y-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Verified connections (Test OK)
              </span>
              <select
                value={selectedRegistryId}
                onChange={(e) => setSelectedRegistryId(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                data-testid="code-connector-registry-attach-select"
              >
                {registryRowsTestOk.map((r) => (
                  <option key={String(r.secretId)} value={String(r.secretId)}>
                    {(r.secretName || r.secretId || '').slice(0, 80)}
                    {r.toolName ? ` (${r.toolName})` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {!registryAttachLoading &&
          selectedRegistryId &&
          registryRowsTestOk.length > 0 &&
          !registryRowsTestOk.some((r) => String(r.secretId) === String(selectedRegistryId)) &&
          registryRows.some((r) => String(r.secretId) === String(selectedRegistryId)) ? (
            <p className="text-[11px] text-amber-800">
              Selected connection isn&apos;t verified — run Test in Connections first.
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="code-connector-registry-mount"
            disabled={
              registryAttachLoading ||
              registryRowsTestOk.length === 0 ||
              !registryRowsTestOk.some((r) => String(r.secretId) === String(selectedRegistryId))
            }
            onClick={confirmRegistryAttach}
            className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-45"
          >
            Mount connection
          </button>
        </div>
      </div>
    </div>
  );
}
